// Sync Schedule status region, with the prototype's markup and copy (VisitsSchedule.css).
import React from 'react';

const TONE = {
  added: ' vs-good',
  partial: ' vs-warn',
  'no-schedule': ' vs-warn',
  ambiguous: ' vs-warn',
  unreadable: ' vs-warn',
  timeout: ' vs-warn',
};
const WORDS = [
  'Zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
];
const countWord = (n) => WORDS[n] || String(n);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const timeOf = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const SyncStatus = ({ status, busy, onClose, onUndo, onRetry, onTryAgain }) => {
  if (!status || !status.kind) return null;
  const forDate = status.dateLabel ? ` for ${status.dateLabel}` : '';
  const working = status.kind === 'checking' || status.kind === 'adding';

  const head = (text) => <p className="vs-head">{text}</p>;
  const body = (text) => <span className="vs-body">{text}</span>;
  const bar = (
    <div className="vs-bar vs-indet">
      <i />
    </div>
  );
  const action = (label, onClick) => (
    <button
      type="button"
      className="vs-btn vs-sync"
      disabled={busy}
      onClick={onClick}
    >
      {label}
    </button>
  );
  const cancelled = status.cancelled?.length
    ? body(`Not added: ${status.cancelled.join(', ')}`)
    : null;
  const tryAgain = (
    <div className="vs-acts">
      {action('Try again', onTryAgain)}
      {body('Nothing was added to your schedule.')}
    </div>
  );

  const content = {
    checking: (
      <>
        {head('Checking your schedule…')}
        {bar}
      </>
    ),
    adding: (
      <>
        {head(`Adding ${plural(status.count, 'visit', 'visits')}${forDate}`)}
        {bar}
      </>
    ),
    added: (
      <>
        {head(`${plural(status.count, 'visit', 'visits')} added${forDate}`)}
        {cancelled}
        <div className="vs-acts">
          <button
            type="button"
            className="vs-linkbtn"
            onClick={() => onUndo(status.ids)}
          >
            Undo
          </button>
        </div>
      </>
    ),
    'up-to-date': (
      <>
        {head('Up to date')}
        {body(
          `All ${plural(status.count, 'appointment', 'appointments')}${forDate} are already in your list.${
            status.syncedAt ? ` Synced today ${timeOf(status.syncedAt)}.` : ''
          }`,
        )}
        {cancelled}
      </>
    ),
    undone: (
      <>
        {head(
          `${status.removed} removed${
            status.kept
              ? `. ${status.kept} kept — ${status.kept === 1 ? 'it already has' : 'they already have'} a recording.`
              : '.'
          }`,
        )}
        {body('Press Sync Schedule again to bring them back.')}
      </>
    ),
    partial: status.rows && (
      <>
        {head(`${status.added} of ${status.total} visits added`)}
        {cancelled}
        {body(
          `${countWord(status.rows.length)} appointment${status.rows.length === 1 ? '' : 's'} didn’t come through:`,
        )}
        {status.rows.map((row, index) => (
          <div key={`${row.label}-${index}`} className="vs-fail">
            <span className="vs-t">{row.label}</span>
            <span>{row.reason}</span>
          </div>
        ))}
        <div className="vs-acts">
          {status.retry.length > 0 &&
            action(
              `Retry ${status.retry.length === 1 ? 'this one' : `these ${status.retry.length}`}`,
              () => onRetry(status.retry),
            )}
          {body(
            `The ${status.added} that worked ${status.added === 1 ? 'is' : 'are'} already in your list.`,
          )}
        </div>
      </>
    ),
    'no-schedule': (
      <>
        {head('We couldn’t find a schedule on that page')}
        {body(
          'Sync Schedule reads whatever page is open in your EMR beside this panel. Open your schedule there, then try again.',
        )}
        {tryAgain}
      </>
    ),
    ambiguous: (
      <>
        {head(status.message)}
        {tryAgain}
      </>
    ),
    unreadable: (
      <>
        {head('We couldn’t read your calendar')}
        {body(
          'It loaded differently than we expected. Refresh your EMR and try again.',
        )}
        <div className="vs-acts">{action('Try again', onTryAgain)}</div>
      </>
    ),
    timeout: body(
      'This is taking longer than expected. Refresh in about 60 seconds to see any visits that were created.',
    ),
  }[status.kind];
  if (!content) return null;

  return (
    <div className={`vs-status${TONE[status.kind] || ''}`}>
      {content}
      {!working && (
        <button
          type="button"
          className="vs-closex"
          aria-label="Close this message"
          onClick={onClose}
        >
          ×
        </button>
      )}
    </div>
  );
};

export default SyncStatus;
